import type { PropsWithChildren } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import PauseIcon from '@mui/icons-material/Pause'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined'
import Accordion from '@mui/material/Accordion'
import AccordionActions from '@mui/material/AccordionActions'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'

import { EntityKpi } from '@components/EntityKpi'
import { formatDate } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import { AssignedBijlagen } from '../../bijlagen/components/AssignedBijlagen'
import type { AssignedBijlage } from '../../bijlagen/types/AssignedBijlage'
import type { Bijlage } from '../../bijlagen/types/Bijlage'
import { type Subproces, SubprocesTypes } from '../types/Subproces'
import {
  getCancelReason,
  getPausedLogEntry,
  getStatusLabelForType,
  isLastSubprocesOfType,
} from '../util/subproces-util'

export { SubprocesCard }

type BaseProps = {
  subproces: Subproces
  subprocessen: Subproces[]
  openLogDrawer: (subproces: Subproces) => void
  onClickCancel: () => void
  onClickPause: () => void
  onClickResume: () => void
  disablePause?: boolean
  disableCancel?: boolean
}

type SubprocesWithBijlagenProps = {
  user: AuthenticatedUser
  isVerantwoordelijke: boolean
  assignedBijlagen: AssignedBijlage[]
  bijlagen: Bijlage[]
  onClickCreateBijlage: () => void
  onClickUploadBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickCancelBijlage: (assignedBijlage: AssignedBijlage | Bijlage) => void
  onClickOpleveren: (assignedBijlage: AssignedBijlage) => void
  onClickReviewBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickEditBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickOpenBijlage: (assignedBijlage: Bijlage) => void
}

// Deze opzet zorgt ervoor dat de props voor bijlagen optioneel zijn, maar mocht
// je er 1 toevoegen, moeten ze allemaal meegegeven worden
type PropsWithBijlagen = PropsWithChildren<
  BaseProps & SubprocesWithBijlagenProps
>
type PropsWithoutBijlagen = PropsWithChildren<
  BaseProps & Partial<Record<keyof SubprocesWithBijlagenProps, never>>
>

const SubprocesCard = ({
  subproces,
  subprocessen,
  openLogDrawer,
  onClickCancel,
  onClickPause,
  onClickResume,
  disablePause = false,
  disableCancel = false,

  user,
  isVerantwoordelijke,
  assignedBijlagen,
  bijlagen,
  onClickCreateBijlage,
  onClickUploadBijlage,
  onClickCancelBijlage,
  onClickOpleveren,
  onClickReviewBijlage,
  onClickEditBijlage,
  onClickOpenBijlage,

  children,
}: PropsWithBijlagen | PropsWithoutBijlagen) => {
  const { status, kpi } = subproces

  const isActive = !['cancelled', 'done'].includes(status)
  const isNotActive = ['cancelled', 'done'].includes(status)
  const isPaused = status === 'paused'
  const isCancelled = status === 'cancelled'
  const isCompleted = status === 'done'

  const pausedLogEntry = getPausedLogEntry({ subproces })
  const cancelReason = getCancelReason({ subproces })
  const isLastOfType = isLastSubprocesOfType({
    subprocessen,
    subproces,
  })

  return (
    <>
      <Accordion defaultExpanded={isLastOfType}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            component="span"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {SubprocesTypes[subproces.type]}

            {isNotActive && (
              <Typography sx={{ marginLeft: 1, color: 'text.secondary' }}>
                {` - ${getStatusLabelForType(
                  subproces.type,
                  subproces.status,
                )}`}
                {cancelReason && ` - ${cancelReason}`}
              </Typography>
            )}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <PropertyRow
                variant="h5"
                label="Subproces"
                value={SubprocesTypes[subproces.type]}
              />

              <PropertyRow
                label="Status"
                value={
                  getStatusLabelForType(subproces.type, subproces.status) +
                  (pausedLogEntry ? ` - ${pausedLogEntry.text}` : '')
                }
              />

              <PropertyRow
                label="Toegewezen aan"
                value={subproces.assignedTo}
              />

              <PropertyRow
                label="Opleverdatum"
                value={formatDate(subproces.dueDate)}
              />

              {kpi && <EntityKpi kpi={kpi} />}
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>{children}</Grid>
          </Grid>
        </AccordionDetails>

        {user && (
          <>
            <Divider sx={{ marginTop: 2 }} />

            {/* @ts-expect-error - bewust een tijdelijke prop om refresh aan te duiden */}
            {subproces.refreshing && <LinearProgress />}

            <AssignedBijlagen
              assignedBijlagen={assignedBijlagen}
              bijlagen={bijlagen}
              isVerantwoordelijke={isVerantwoordelijke}
              showCancelAssignedBijlage={
                isVerantwoordelijke && !isCompleted && !isCancelled
              }
              showCreateAssignedBijlage={!isCompleted && !isCancelled}
              onCreate={onClickCreateBijlage}
              onUpload={onClickUploadBijlage}
              onCancel={onClickCancelBijlage}
              onOpleveren={onClickOpleveren}
              onOpen={onClickOpenBijlage}
              onReview={onClickReviewBijlage}
              onEdit={onClickEditBijlage}
              user={user}
            />
          </>
        )}

        <AccordionActions>
          {isActive && (
            <>
              <Button
                variant="text"
                startIcon={<CloseIcon />}
                onClick={onClickCancel}
                disabled={disableCancel}
              >
                Annuleren
              </Button>
            </>
          )}

          {isActive && !isPaused && (
            <Button
              variant="text"
              disabled={disablePause}
              startIcon={<PauseIcon />}
              onClick={onClickPause}
            >
              Pauzeren
            </Button>
          )}

          {isPaused && (
            <Button
              variant="text"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={onClickResume}
            >
              Hervatten
            </Button>
          )}

          <Box
            sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              variant="text"
              startIcon={<InfoOutlineIcon />}
              onClick={() => openLogDrawer(subproces)}
            >
              Log
            </Button>
          </Box>
        </AccordionActions>
      </Accordion>
    </>
  )
}
